import React, {
  startTransition,
  useDeferredValue,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import OrderDetailDrawer from '../components/admin/OrderDetailDrawer';
import { useToast } from '../components/common/ToastProvider';
import { hasApiConfig } from '../config/artTasteConfig';
import { adminTabs } from '../data/content';
import {
  formatCurrency,
  formatDate,
  getOrderDisplayId,
  getStatusMeta,
} from '../lib/format';
import {
  getFriendlyErrorMessage,
  getCurrentUser,
  isAdminUser,
  listOrders,
  signInAdmin,
  signOutAdmin,
  updateOrderStatus,
} from '../lib/artTasteApi';

function nextStatusFor(status) {
  const flow = {
    paid: 'printed',
    printed: 'delivered',
    delivered: 'paid',
    cancelled: 'paid',
    simulated: 'printed',
  };

  return flow[status] || 'printed';
}

function downloadFile(url, name) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.target = '_blank';
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export default function AdminPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });
  const [loginError, setLoginError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const { showToast } = useToast();

  const loadOrders = async () => {
    setOrdersLoading(true);

    try {
      const nextOrders = await listOrders();
      startTransition(() => {
        setOrders(nextOrders);
      });
      setLastRefreshed(new Date().toLocaleTimeString('en-IN'));
    } catch (error) {
      showToast(getFriendlyErrorMessage(error, 'Could not load orders.'), 'error');
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    const user = getCurrentUser();

    if (isAdminUser(user)) {
      setAdminUser(user);
      loadOrders();
    } else {
      setAdminUser(null);
      if (user) {
        setLoginError('Admin access required. Please sign in with an admin account.');
      }
    }
    setAuthLoading(false);
  }, []);

  const paidOrders = orders.filter((order) => order.paymentStatus === 'paid');

  const stats = {
    total: paidOrders.length,
    revenue: paidOrders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0),
    pending: paidOrders.filter((order) =>
      ['paid', 'printed'].includes(order.status || 'paid'),
    ).length,
    delivered: paidOrders.filter((order) => order.status === 'delivered').length,
    cancelled: paidOrders.filter((order) => order.status === 'cancelled').length,
    avgOrderValue: paidOrders.length > 0
      ? (
        paidOrders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0) / paidOrders.length
      ).toFixed(2)
      : 0,
  };

  const getRecentOrders = () => {
    return orders.slice().reverse().slice(0, 5);
  };

  const getTopCustomers = () => {
    const customerMap = {};
    paidOrders.forEach((order) => {
      const name = order.customer?.name || 'Unknown';
      if (!customerMap[name]) {
        customerMap[name] = { name, count: 0, revenue: 0 };
      }
      customerMap[name].count += 1;
      customerMap[name].revenue += Number(order.totalAmount || 0);
    });
    return Object.values(customerMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  const toggleStatusFilter = (statusValue) => {
    if (statusFilter === statusValue) {
      setStatusFilter('');
    } else {
      setStatusFilter(statusValue);
    }
  };

  const filteredOrders = orders
    .filter((order) => {
      const searchValue = deferredSearchTerm.trim().toLowerCase();
      const orderStatus = order.status || 'paid';
      const deliveryMode = order.printOptions?.delivery || 'standard';

      const matchesSearch = searchValue
        ? [order.orderId, order.id, order.customer?.name, order.customer?.email, order.customer?.phone]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchValue))
        : true;

      const matchesStatus = statusFilter ? orderStatus === statusFilter : true;
      const matchesDelivery = deliveryFilter ? deliveryMode === deliveryFilter : true;

      return matchesSearch && matchesStatus && matchesDelivery;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'date-asc':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'amount-desc':
          return Number(b.totalAmount || 0) - Number(a.totalAmount || 0);
        case 'amount-asc':
          return Number(a.totalAmount || 0) - Number(b.totalAmount || 0);
        case 'name-asc':
          return (a.customer?.name || '').localeCompare(b.customer?.name || '');
        default:
          return 0;
      }
    });

  const selectedOrder = orders.find((order) => order.orderId === selectedOrderId) || null;
  const currentTabTitle =
    adminTabs.find((tab) => tab.key === activeTab)?.label || 'All Orders';

  const setTab = (tabKey) => {
    const matchingTab = adminTabs.find((tab) => tab.key === tabKey);
    setActiveTab(tabKey);
    setIsSidebarOpen(false);
    if (matchingTab?.status) {
      setStatusFilter(matchingTab.status);
    } else {
      setStatusFilter('');
    }
    setSearchTerm('');
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError('');

    if (!loginForm.email || !loginForm.password) {
      setLoginError('Please enter both email and password.');
      return;
    }

    try {
      const user = await signInAdmin(loginForm.email, loginForm.password);
      setAdminUser(user);
      setLoginForm({
        email: '',
        password: '',
      });
      showToast('Admin signed in successfully.', 'success');
      loadOrders();
    } catch (error) {
      setLoginError(
        getFriendlyErrorMessage(error, 'We could not sign you in to the admin dashboard.'),
      );
    }
  };

  const handleLogout = async () => {
    try {
      await signOutAdmin();
      setAdminUser(null);
      setOrders([]);
      setActiveTab('dashboard');
      showToast('Signed out.', 'info');
    } catch (error) {
      showToast(getFriendlyErrorMessage(error, 'Could not sign out.'), 'error');
    }
  };

  const patchLocalOrderStatus = (orderId, nextStatus) => {
    setOrders((currentOrders) =>
      currentOrders.map((order) =>
        order.orderId === orderId
          ? {
            ...order,
            status: nextStatus,
          }
          : order,
      ),
    );
  };

  const handleStatusUpdate = async (orderId, nextStatus) => {
    try {
      await updateOrderStatus(orderId, nextStatus);
      patchLocalOrderStatus(orderId, nextStatus);
      showToast(`Status updated to ${nextStatus}.`, 'success');
      setSelectedOrderId('');
    } catch (error) {
      showToast(getFriendlyErrorMessage(error, 'Could not update status.'), 'error');
    }
  };

  const handleQuickStatus = async (order) => {
    const nextStatus = nextStatusFor(order.status || 'paid');
    await handleStatusUpdate(order.orderId, nextStatus);
  };

  const downloadAllFiles = (order) => {
    const files = order.files || [];

    if (!files.length) {
      showToast('This order does not have any files attached.', 'error');
      return;
    }

    files.forEach((file, index) => {
      window.setTimeout(() => {
        downloadFile(file.url, file.name);
      }, index * 250);
    });

    showToast(`Downloading ${files.length} file(s).`, 'success');
  };

  if (authLoading) {
    return (
      <div className="admin-loading-screen">
        <div className="loading-panel">
          <span className="spinner" />
          <strong>Loading admin dashboard...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-shell">
      {!hasApiConfig ? (
        <div className="setup-banner tone-warning">
          <span>
            Backend API is required for the admin dashboard. Add your REACT_APP_API_URL
            value in Art-Taste/.env first.
          </span>
        </div>
      ) : null}

      {!adminUser ? (
        <div className="admin-login-screen">
          <form className="admin-login-card" onSubmit={handleLoginSubmit}>
            <div className="brand-mark admin-brand">
              Art<span>-</span>Taste
            </div>
            <p>Admin dashboard sign-in</p>

            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={loginForm.email}
                onChange={(event) =>
                  setLoginForm((currentForm) => ({
                    ...currentForm,
                    email: event.target.value,
                  }))
                }
                placeholder="admin@art-taste.in"
                disabled={!hasApiConfig}
              />
            </label>

            <label className="field">
              <span>Password</span>
              <input
                type="password"
                value={loginForm.password}
                onChange={(event) =>
                  setLoginForm((currentForm) => ({
                    ...currentForm,
                    password: event.target.value,
                  }))
                }
                placeholder="Enter your password"
                disabled={!hasApiConfig}
              />
            </label>

            {loginError ? <div className="inline-alert danger">{loginError}</div> : null}

            <button type="submit" className="btn-primary full-width" disabled={!hasApiConfig}>
              Sign In
            </button>

            <Link to="/" className="admin-login-link">
              Back to storefront
            </Link>
          </form>
        </div>
      ) : (
        <div className="admin-layout">
          <div
            className={`admin-sidebar-overlay ${isSidebarOpen ? 'open' : ''}`}
            onClick={() => setIsSidebarOpen(false)}
            role="presentation"
          />
          <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
            <div className="brand-mark admin-sidebar-brand">
              Art<span>-</span>Taste
            </div>
            <p className="admin-sidebar-label">Admin Panel</p>

            <button
              type="button"
              className="admin-sidebar-close"
              onClick={() => setIsSidebarOpen(false)}
            >
              Close
            </button>

            <nav className="admin-sidebar-nav" aria-label="Admin sections">
              {adminTabs && adminTabs.length > 0 ? (
                adminTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`admin-nav-item ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))
              ) : (
                <div style={{ padding: '1rem', color: '#ddd' }}>No tabs</div>
              )}
            </nav>

            <div className="admin-sidebar-footer">
              <small>{adminUser.email}</small>
              <button type="button" className="btn-secondary full-width" onClick={handleLogout}>
                Sign Out
              </button>
              <Link to="/" className="admin-home-link">
                View Storefront
              </Link>
            </div>
          </aside>

          <section className="admin-main">
            <div className="admin-mobile-toolbar">
              <button
                type="button"
                className="admin-menu-toggle"
                onClick={() => setIsSidebarOpen(true)}
              >
                Menu
              </button>
            </div>
            <header className="admin-topbar">
              <div>
                <h1>{currentTabTitle}</h1>
                <p>{lastRefreshed ? `Last refreshed at ${lastRefreshed}` : 'Live order overview'}</p>
              </div>
              <button type="button" className="btn-primary" onClick={loadOrders}>
                Refresh
              </button>
            </header>

            <div className="admin-content">
              {activeTab === 'dashboard' ? (
                <>
                  {/* Admin Dashboard Indicator */}
                  <div style={{
                    padding: '0.75rem 1rem',
                    marginBottom: '1rem',
                    backgroundColor: 'rgba(192, 74, 30, 0.1)',
                    borderRadius: '4px',
                    borderLeft: '3px solid var(--rust)'
                  }}>
                    <strong style={{ color: 'var(--rust)' }}>ADMIN DASHBOARD</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--muted)' }}>
                      This is the admin analytics view showing business metrics and recent orders
                    </p>
                  </div>

                  {/* Dashboard Stats */}
                  <div className="admin-stats-grid">
                    <div className="admin-stat-card accent">
                      <span>Total Orders</span>
                      <strong>{stats.total}</strong>
                      <small>All time</small>
                    </div>
                    <div className="admin-stat-card gold">
                      <span>Revenue</span>
                      <strong>{formatCurrency(stats.revenue)}</strong>
                      <small>Total collected</small>
                    </div>
                    <div className="admin-stat-card">
                      <span>Avg Order Value</span>
                      <strong>{formatCurrency(stats.avgOrderValue)}</strong>
                      <small>Per order</small>
                    </div>
                    <div className="admin-stat-card">
                      <span>Pending Print</span>
                      <strong>{stats.pending}</strong>
                      <small>Need attention</small>
                    </div>
                    <div className="admin-stat-card green">
                      <span>Delivered</span>
                      <strong>{stats.delivered}</strong>
                      <small>Completed</small>
                    </div>
                    <div className="admin-stat-card">
                      <span>Cancelled</span>
                      <strong>{stats.cancelled}</strong>
                      <small>Orders cancelled</small>
                    </div>
                  </div>

                  {/* Recent Orders Section */}
                  <div className="admin-dashboard-section">
                    <h3>Recent Orders</h3>
                    <div className="admin-recent-orders">
                      {getRecentOrders().length ? (
                        getRecentOrders().map((order) => {
                          const statusMeta = getStatusMeta(order.status);
                          return (
                            <div
                              key={order.orderId}
                              className="recent-order-item"
                              onClick={() => setSelectedOrderId(order.orderId)}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="recent-order-header">
                                <strong>{getOrderDisplayId(order)}</strong>
                                <span className={`status-pill tone-${statusMeta.tone}`}>
                                  {statusMeta.label}
                                </span>
                              </div>
                              <div className="recent-order-details">
                                <span>{order.customer?.name || 'Unknown'}</span>
                                <span>•</span>
                                <span>{formatCurrency(order.totalAmount || 0)}</span>
                                <span>•</span>
                                <span>{formatDate(order.createdAt)}</span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="empty-state">No orders yet</div>
                      )}
                    </div>
                  </div>

                  {/* Top Customers Section */}
                  <div className="admin-dashboard-section">
                    <h3>Top Customers</h3>
                    <div className="admin-top-customers">
                      {getTopCustomers().length ? (
                        getTopCustomers().map((customer, idx) => (
                          <div key={idx} className="customer-row">
                            <div className="customer-info">
                              <strong>{customer.name}</strong>
                              <small>{customer.count} orders</small>
                            </div>
                            <div className="customer-revenue">
                              {formatCurrency(customer.revenue)}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="empty-state">No customer data</div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Orders Table View */}
                  <div className="admin-filters">
                    <input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search by order ID, name, email, or phone"
                    />
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                      <option value="">All statuses</option>
                      <option value="paid">Paid</option>
                      <option value="printed">Printed</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <select
                      value={deliveryFilter}
                      onChange={(event) => setDeliveryFilter(event.target.value)}
                    >
                      <option value="">All delivery modes</option>
                      <option value="standard">Standard</option>
                      <option value="express">Express</option>
                    </select>
                    <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                      <option value="date-desc">Newest First</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="amount-desc">Highest Amount</option>
                      <option value="amount-asc">Lowest Amount</option>
                      <option value="name-asc">Customer Name</option>
                    </select>
                  </div>

                  <div className="admin-table-card">
                    <div className="admin-table-scroll">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>Customer</th>
                            <th>Files</th>
                            <th>Print</th>
                            <th>Amount</th>
                            <th>Delivery</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ordersLoading ? (
                            <tr>
                              <td colSpan="9" className="table-empty">
                                Loading orders...
                              </td>
                            </tr>
                          ) : filteredOrders.length ? (
                            filteredOrders.map((order) => {
                              const statusMeta = getStatusMeta(order.status);
                              const files = order.files || [];
                              return (
                                <tr key={order.orderId}>
                                  <td className="order-code">{getOrderDisplayId(order)}</td>
                                  <td>
                                    <strong>{order.customer?.name || '--'}</strong>
                                    <small>{order.customer?.phone || '--'}</small>
                                  </td>
                                  <td>{files.length}</td>
                                  <td>
                                    <strong>
                                      {order.printOptions?.type === 'color'
                                        ? 'Color'
                                        : 'Black and White'}
                                    </strong>
                                    <small>
                                      {order.printOptions?.size || 'A4'} / {order.printOptions?.pages || 1}p x{' '}
                                      {order.printOptions?.copies || 1}
                                    </small>
                                  </td>
                                  <td>{formatCurrency(order.totalAmount || 0)}</td>
                                  <td>{order.printOptions?.delivery || 'standard'}</td>
                                  <td>
                                    <span className={`status-pill tone-${statusMeta.tone}`}>
                                      {statusMeta.label}
                                    </span>
                                  </td>
                                  <td>{formatDate(order.createdAt)}</td>
                                  <td>
                                    <div className="row-actions">
                                      <button
                                        type="button"
                                        className="inline-action"
                                        onClick={() => setSelectedOrderId(order.orderId)}
                                      >
                                        View
                                      </button>
                                      <button
                                        type="button"
                                        className="inline-action"
                                        onClick={() => downloadAllFiles(order)}
                                      >
                                        Files
                                      </button>
                                      <button
                                        type="button"
                                        className="inline-action danger"
                                        onClick={() => handleQuickStatus(order)}
                                      >
                                        Status
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="9" className="table-empty">
                                No orders match the current filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          <OrderDetailDrawer
            order={selectedOrder}
            isOpen={Boolean(selectedOrder)}
            onClose={() => setSelectedOrderId('')}
            onStatusUpdate={handleStatusUpdate}
          />
        </div>
      )}
    </div>
  );
}
